import React, { useContext, useState, useEffect, useRef, useCallback } from 'react';
import { Router } from '@com/core/common/translations/i18n';
import { TCategoryData } from '@com/core/common/types/navigation/navigation';
import { setCookie } from '@com/core/common/utils/client';
import sivviConfig from '@com/core/sivvi/sivviConfig';
import { TCategory } from '@com/core/common/types/config';
import NavConfig from '@com/core/common/utils/navConfig';
import { getPageForPath, removeStartingSlash } from '@com/core/common/utils/url';
import { animateScroll as scroll } from 'react-scroll';
import { AppContext } from './appContext';

type TProps = {
    children: JSX.Element;
};

export type TNavContext = {
    // TODO @Harshal - refactor this as components are not
    // supposed to be imported/exported within contexts
    navTo: (page: string, options?: unknown) => void;
    categoryData: TCategoryData;
    isLoading: boolean;
    setDefaultCategory: (category: TCategory) => void;
    setLoadingState: (loadingState: boolean) => void;
    setKeepLoading: (keepLoadingState: boolean) => void;
};

// eslint-disable-next-line
export const NavContext = React.createContext({} as TNavContext);

export const NavContextProvider = ({ children: contextChildren }: TProps): React.ReactElement => {
    const {
        locale: { apiLocale },
    } = useContext(AppContext);

    const categoryData = NavConfig.navConfig[apiLocale];

    // #region - Loader
    const [isLoading, setIsLoading] = useState(false);
    const [keepLoading, setKeepLoading] = useState(false);

    const loaderTimeoutToken = useRef(null);

    const clearLoadingTimer = () => {
        clearTimeout(loaderTimeoutToken.current);
    };

    const setLoadingState = useCallback((loadingState = true): void => {
        setIsLoading(loadingState);

        // if false, clear the timeout
        if (!loadingState) {
            clearLoadingTimer();
        }
    }, []);

    const onRouteChangeStart = useCallback(() => {
        scroll.scrollToTop({ smooth: true });
        setLoadingState(true);
    }, [setLoadingState]);

    const onRouteChangeError = useCallback(() => {
        if (!keepLoading) {
            setLoadingState(false);
        }
    }, [setLoadingState, keepLoading]);
    const onRouteChangeComplete = useCallback(() => {
        if (!keepLoading) {
            setLoadingState(false);
        }
    }, [setLoadingState, keepLoading]);

    useEffect(() => {
        Router.events.on('routeChangeStart', onRouteChangeStart);
        Router.events.on('routeChangeError', onRouteChangeError);
        Router.events.on('routeChangeComplete', onRouteChangeComplete);

        const cleanup = () => {
            Router.events.off('routeChangeStart', onRouteChangeStart);
            Router.events.off('routeChangeError', onRouteChangeError);
            Router.events.off('routeChangeComplete', onRouteChangeComplete);
        };
        return cleanup;
    }, [onRouteChangeStart, onRouteChangeError, onRouteChangeComplete]);

    useEffect(() => {
        if (isLoading) {
            // automatically hide loader after 15s
            loaderTimeoutToken.current = setTimeout(() => {
                setIsLoading(false);
            }, 15 * 1000);
        }

        return clearLoadingTimer;
    }, [isLoading]);
    // #endregion

    // #region - Sivvi default category
    const { categories } = sivviConfig;
    const setDefaultCategory = (category: TCategory) => {
        const cat = category.toLocaleLowerCase() as TCategory;

        if (categories.indexOf(cat) !== -1) {
            setCookie('defaultCategory', cat);
            Router.push(`/${cat}`);
        }
    };
    // #endregion

    type TNavToOptions = {
        shallow?: boolean;
        replace?: boolean;
    };

    const navTo = async (path = '', options: TNavToOptions = {}): Promise<boolean> => {
        const { replace = false, shallow = false } = options;
        const routerMethod = replace ? 'replace' : 'push';

        const sanitizedPath = `/${removeStartingSlash(path)}`;

        const routerOptions = { shallow };

        const page = await getPageForPath(sanitizedPath);

        return Router[routerMethod](
            {
                pathname: page,
            },
            sanitizedPath,
            routerOptions,
        );
    };

    const contextValue: TNavContext = {
        navTo,
        categoryData,
        isLoading,
        setDefaultCategory,
        setLoadingState,
        setKeepLoading,
    };

    return <NavContext.Provider value={contextValue}>{contextChildren}</NavContext.Provider>;
};
